import { formatRoute } from 'constants/urls'
import { LEARNING_ACCOUNT_TYPE } from 'constants/accountTypes'
import { isNil } from 'lodash'
import { replace } from 'redux-first-history'

import { ACCEPT_INVITATION, ACTIVATION_INTERSTITIAL, MY_5_DYNAMICS, WELCOME } from 'urls'
import { EXPIRATION_STRATEGY } from 'app_modules/accounts/constants'
import { isDateExpired } from 'helpers'
import { updateState } from 'app_modules/assessment/actions'
import * as notificationActions from 'app_modules/notifications/actions'
import actionTypes from 'app_modules/session/constants'

import { fetchDashboard } from './fetchDashboard'
import { fetchVideos } from './fetchVideos'
import { logout } from './fetchLogout'
import { setCurrentProfile } from './setProfile'
import acceptInvitation from '../../invites/actions/fetchAcceptInvitation'

// Actions

export const setCurrentUser = user => ({
  payload: user,
  type: actionTypes.SET_CURRENT_USER,
})

export const setImpersonateUser = user => ({
  payload: user,
  type: actionTypes.SET_USER_BY_IMPERSONATION,
})

// Thunks

export const setUser = (user, isInvitationPending = false, isInvitationPreAccepted = false, invitationId) => {
  const thunk = dispatch => {
    const {
      defaultAccount,
      hasActiveAssessment: isCompleted,
      hasOngoingAssessment: isInProgress,
      profiles,
      impersonateToken,
    } = user

    let userDispatch = setCurrentUser
    let defaultProfile
    let isExpired = false

    if (impersonateToken) {
      userDispatch = setImpersonateUser

      defaultProfile = profiles.find(profile => profile.account.isPersonal) || profiles[0]
    } else {
      defaultProfile = profiles.find(({ account }) => account.id === defaultAccount.id)
      isExpired =
        defaultAccount &&
        !defaultProfile?.limitedMember &&
        defaultAccount?.expirationStrategy !== EXPIRATION_STRATEGY.DO_NOTHING &&
        !!defaultProfile?.license &&
        isDateExpired(defaultProfile.license.expiresAt)
    }
    if (isNil(defaultProfile) || (isExpired && !isInvitationPending && !isInvitationPreAccepted)) {
      return Promise.all([dispatch(logout()), dispatch(notificationActions.notifyWarning({ label: 'accountExpired' }))])
    }

    const promises = [dispatch(updateState({ isCompleted, isInProgress })), dispatch(userDispatch(user))]

    if (!isExpired || isInvitationPreAccepted) {
      promises.push(dispatch(setCurrentProfile(defaultProfile)), dispatch(fetchVideos()), dispatch(fetchDashboard()))
    }

    const handleOnAcceptInvitation = () => {
      dispatch(replace(formatRoute(MY_5_DYNAMICS, { accountId: invitationId })))
    }

    return Promise.all(promises).then(() => {
      if (!isExpired) {
        if (isCompleted) {
          // Skip interstitial for impersonation — admins should land on the dashboard directly.
          // Skip interstitial for E6 users who already have an active re-engagement segment
          // (active/needs_circle/power) — they don't need the interstitial flow again.
          const hasNonInterstitialSegment =
            defaultProfile.reEngagementSegment && defaultProfile.reEngagementSegment !== 'dormant'
          if (
            !impersonateToken &&
            !defaultProfile.interstitialCompletedAt &&
            !hasNonInterstitialSegment &&
            !defaultProfile.limitedMember &&
            defaultProfile.account.accountType !== LEARNING_ACCOUNT_TYPE
          ) {
            dispatch(replace(formatRoute(ACTIVATION_INTERSTITIAL, { accountId: defaultProfile.account.id })))
          } else {
            dispatch(replace(formatRoute(MY_5_DYNAMICS, { accountId: defaultProfile.account.id })))
          }
        } else {
          dispatch(replace(formatRoute(WELCOME, { accountId: defaultProfile.account.id })))
        }
      } else if (isInvitationPreAccepted) {
        dispatch(acceptInvitation(true, handleOnAcceptInvitation))
      } else {
        dispatch(replace(formatRoute(ACCEPT_INVITATION)))
      }
    })
  }

  thunk.type = actionTypes.SET_USER

  return thunk
}

export default setUser
